import { useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { HomePage } from "./features/home/HomePage";
import { ProjectPage } from "./features/project/ProjectPage";
import { AdminPage } from "./features/admin/AdminPage";
import { ReportPage } from "./features/report/ReportPage";
import { SharePage } from "./features/share/SharePage";
import { LoginPage, RegisterPage, VerifyPage, ForgotPage, ResetPage } from "./auth/AuthPages";
import { AccountPage } from "./auth/AccountPage";
import { PlansPage } from "./billing/PlansPage";
import { PrivacyPage, TermsPage } from "./features/legal/LegalPages";
import { DistanceSalesPage, PreInfoPage } from "./features/legal/ContractPages";
import { GuidePage } from "./features/legal/GuidePage";
import { useProvider } from "./provider/useProvider";
import { ApiProviderStore, providerStore } from "./provider";
import { useAuth } from "./auth/useAuth";
import { startAutosave, useProjectStore } from "./state/projectStore";
import { cloudStore } from "./storage";
import { ErrorBoundary } from "./ui/ErrorBoundary";
import { startTabSync } from "./state/tabSync";
import { UnsavedDialog } from "./features/project/UnsavedDialog";

/** Oturum durumuna göre sağlayıcı deposunu seçer: API varsa sunucu ayarları, yoksa v1.0 yerel ayar. */
export function useBootstrap(): void {
  const status = useAuth((s) => s.status);
  const role = useAuth((s) => s.user?.role);
  useEffect(() => startAutosave(), []);
  useEffect(() => startTabSync(), []);
  useEffect(() => { void useAuth.getState().load(); }, []);
  useEffect(() => {
    if (status === "unknown") return;
    const prov = useProvider.getState();
    if (status === "no-api") prov.useStore(providerStore, false);
    else prov.useStore(new ApiProviderStore({ isAdmin: () => useAuth.getState().user?.role === "admin", isAuthed: () => useAuth.getState().status === "authed" }), true);
    void useProvider.getState().load();
  }, [status, role]);
  // Bağlantı gelince çevrimdışı kuyruğu gönder
  useEffect(() => {
    const flush = (): void => { if (useAuth.getState().status === "authed") void cloudStore.flush().then(({ sent }) => { const st = useProjectStore.getState(); if (st.project && sent.includes(st.project.id) && st.sync === "offline") st.setSync("saved"); }); };
    window.addEventListener("online", flush);
    return () => window.removeEventListener("online", flush);
  }, []);
}

export function App(): JSX.Element {
  useBootstrap();
  return (
    <ErrorBoundary>
      <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, "")}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/proje/:id/rapor" element={<ReportPage />} />
          <Route path="/proje/:id/*" element={<ProjectPage />} />
          <Route path="/yonetim" element={<AdminPage />} />
          <Route path="/giris" element={<LoginPage />} />
          <Route path="/kayit" element={<RegisterPage />} />
          <Route path="/dogrula" element={<VerifyPage />} />
          <Route path="/sifre" element={<ForgotPage />} />
          <Route path="/sifre-sifirla" element={<ResetPage />} />
          <Route path="/hesap" element={<AccountPage />} />
          <Route path="/planlar" element={<PlansPage />} />
          <Route path="/paylasim/:token/rapor" element={<SharePage report />} />
          <Route path="/paylasim/:token" element={<SharePage />} />
          <Route path="/gizlilik" element={<PrivacyPage />} />
          <Route path="/kullanim-sartlari" element={<TermsPage />} />
          <Route path="/on-bilgilendirme" element={<PreInfoPage />} />
          <Route path="/mesafeli-satis" element={<DistanceSalesPage />} />
          <Route path="/kilavuz" element={<GuidePage />} />
        </Routes>
      </BrowserRouter>
      {/* Kapatma diyaloğu her sayfada (CAD kabuğu, vitrin): tek proje açık, kapatmadan önce sorulur */}
      <UnsavedDialog />
    </ErrorBoundary>
  );
}
